export class CacheMap<V extends { code?: string; [key: string]: any }> extends Map<string, V> {
  constructor(elements?: V[], private idKey?: string, private keepIds: string[] = []) {
    super();
    if (!elements || !idKey) return;
    for (const elem of elements) {
      this.set(elem[idKey], elem);
    }
  }

  /**
   * Whether the size of a map is greater than 0
   */
  get isNotEmpty(): boolean {
    return this.size > 0;
  }

  /**
   * Whether the size of a map is equal to 0
   */
  get isEmpty(): boolean {
    return this.size == 0;
  }

  /**
   * Returns the value of the first element in the map that has given code, and undefined otherwise.
   */
  findByCode(code: string, keepAll = false): V | undefined {
    return [...this.values(keepAll)].find((elem) => elem.code == code);
  }

  /**
   * Returns the value of the first element in the map that has given value for the property specified, and undefined otherwise.
   */
  findByProp(prop: string, value: string, keepAll = false): V | undefined {
    return [...this.values(keepAll)].find((elem) => elem[prop] == value);
  }

  /**
   * Sorts a map using the given callback and returns a new instance of CacheMap
   */
  sort(callback: (a: V, b: V) => number, keepAll = false): CacheMap<V> {
    return new CacheMap([...this.values(keepAll)].sort(callback), this.idKey, this.keepIds);
  }

  /**
   * Returns a new instance of CacheMap that contains all the elements that meet the condition specified in a callback function.
   * If the params keepId is passed, it filters in all non deleted elements and the one corresponding the given id, either if is deleted or not
   */
  filter(callback: (value: V, index: number, array: V[]) => boolean, keepAll = false): CacheMap<V> {
    return new CacheMap([...this.values(keepAll)].filter(callback), this.idKey, this.keepIds);
  }

  /**
   * Loops a cache map and for each value calls the callback provided
   */
  loop(callback: (value: V) => void, keepAll = false) {
    [...this.values(keepAll)].forEach(callback);
  }

  /**
   * @override
   * if keepAll is true returns all entities, deleted and non deleted.
   * Otherwise returns all non deleted entity and the ones corresponding keepsIds, either if they are deleted or not.
   */
  override values(keepAll = false): IterableIterator<V> {
    if (keepAll) return Map.prototype.values.call(this);
    return [...Map.prototype.values.call(this)].filter((x) => !x.deleted || this.keepIds.includes(x[this.idKey!])).values();
  }

  /**
   * Returns instance of CacheMap with all non deleted values and value with given id, either if is deleted or not
   */
  with(...keepIds: string[]) {
    return new CacheMap([...this.values(true)], this.idKey, keepIds);
  }
}
